"use client"

import * as Sentry from "@sentry/nextjs"
import { useEffect, useState } from "react"
import { isVersionSkewError } from "@/lib/deploy/versionSkew"
import { VersionUpdateScreen } from "@/components/VersionUpdateScreen"
import { nativeSafeReload } from "@/lib/capacitor/nav"

// Root-level error boundary. Catches any exception thrown BELOW the root
// layout that no nested error.tsx handles — most importantly the per-venue
// SlugLayout (app/[slug]/layout.tsx) and the post-login portfolio landings
// (/home, /me). Before this file existed, those failures escaped every
// boundary and tripped global-error.tsx, which replaces the ROOT layout and
// drops the whole app shell — on the iOS WebView wrapper that surfaced as a
// full-page crash whose "Ir al inicio" ejected the operator to the marketing
// site. This boundary renders WITHIN the shell, so the failure stays
// recoverable and in-app.
//
// Strings are hardcoded (not next-intl) on purpose: an error screen must not
// itself depend on the i18n provider having mounted. Same stance as
// global-error.tsx.
export default function AppError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    Sentry.captureException(error, { tags: { zone: "app-root" } })
  }, [error])

  function copyDigest() {
    if (!error.digest) return
    void navigator.clipboard?.writeText(error.digest).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }

  // A hard reload re-fetches the bundle from the current deployment, which is
  // the actual fix when the crash came from a stale WebView cache hitting a
  // newer server (deployment skew). reset() alone would re-render the same
  // stale tree and re-throw.
  function reload() {
    // nativeSafeReload: en el shell iOS un reload de documento completo fuera
    // del prefijo /tablet se entrega a Safari, así que el botón de la tarjeta
    // de error no hacía nada dentro de la app. Ver lib/capacitor/nav.ts.
    nativeSafeReload()
  }

  if (isVersionSkewError(error)) return <VersionUpdateScreen />

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center px-6 py-10 text-center"
      style={{ background: "var(--redy-warm-white)", color: "var(--redy-charcoal)" }}
    >
      <div className="w-full max-w-[480px]">
        <p
          className="text-[10.5px] font-bold uppercase tracking-[0.2em]"
          style={{ color: "var(--redy-red)" }}
        >
          Algo se rompió
        </p>
        <h1
          className="mt-3 text-[32px] font-extrabold leading-[1.05]"
          style={{
            fontFamily: "'Satoshi', 'Inter', system-ui, sans-serif",
            letterSpacing: "-0.02em",
          }}
        >
          Tuvimos un{" "}
          <span
            style={{
              background: "var(--redy-gradient)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            problema
          </span>
          .
        </h1>
        <p className="mt-4 text-[15px]" style={{ color: "var(--redy-secondary)" }}>
          Recarga para continuar. Si vuelve a pasar, envía el código de abajo a soporte.
        </p>
        {error.digest && (
          <button
            type="button"
            onClick={copyDigest}
            className="mt-6 inline-block rounded-lg px-3 py-2 font-mono text-[12px]"
            style={{ background: "rgba(0,0,0,0.04)", color: "var(--redy-muted)" }}
          >
            {copied ? "✓ Código copiado" : `Código: ${error.digest} · tap para copiar`}
          </button>
        )}
        <div className="mt-7 flex flex-wrap justify-center gap-3">
          <button
            type="button"
            onClick={reload}
            className="rounded-2xl px-7 py-3.5 text-[15px] font-black text-white transition-all active:scale-[0.99]"
            style={{
              background: "var(--redy-gradient)",
              boxShadow: "0 8px 24px rgba(215,38,61,0.22)",
            }}
          >
            Recargar
          </button>
          <a
            href="/home"
            className="rounded-2xl px-7 py-3.5 text-[15px] font-semibold transition-all active:scale-[0.99]"
            style={{
              background: "var(--redy-surface)",
              color: "var(--redy-charcoal)",
              border: "1px solid var(--redy-border)",
            }}
          >
            Ir al inicio
          </a>
        </div>
      </div>
    </div>
  )
}
